import { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/hooks/usePermissions';
import * as repo from '../data/votingRepository';
import { useVotingMutation } from '../hooks/useVoting';
import type { ElectionState, RoundSettings, StatePosition, StateRace } from '../types';
import { candidateName, positionStep, racesOfPosition } from '../utils/rounds';
import { RoundCard } from './RoundCard';
import { RoundDialog } from './RoundDialog';

interface PositionsPanelProps {
  state: ElectionState;
  fetchedAt: number;
}

export function PositionsPanel({ state, fetchedAt }: PositionsPanelProps) {
  const { can } = usePermissions();
  const electionId = state.election.id;
  const [title, setTitle] = useState('');
  const [dialog, setDialog] = useState<{ position: StatePosition; race: StateRace | null } | null>(null);

  const addPosition = useVotingMutation(electionId, (t: string) => repo.addPosition(electionId, t, state.positions.length + 1), {
    success: 'Cargo agregado',
  });
  const removePosition = useVotingMutation(electionId, (id: string) => repo.removePosition(id), { success: 'Cargo eliminado' });
  const saveRound = useVotingMutation(
    electionId,
    (a: { race: StateRace | null; positionId: string; ids: string[]; settings: RoundSettings }) =>
      a.race ? repo.updateRace(a.race.id, a.ids, a.settings) : repo.createRace(electionId, a.positionId, a.ids, a.settings),
    { success: 'Ronda guardada' },
  );

  const election = state.election;
  const closed = election.status === 'CLOSED' || election.status === 'ARCHIVED';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cargos y rondas</CardTitle>
        <CardDescription>
          La sala define el cargo (por ejemplo Presidente) y los candidatos de cada ronda. Si nadie llega al porcentaje, o hay empate,
          se crea otra ronda para el mismo cargo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {can('election_race.create') && !closed && (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim().length < 2) return;
              addPosition.mutate(title.trim(), { onSuccess: () => setTitle('') });
            }}
          >
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nuevo cargo, ej. Presidente" />
            <Button type="submit" disabled={addPosition.isPending || title.trim().length < 2}>
              {addPosition.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Plus className="mr-2 size-4" />} Agregar cargo
            </Button>
          </form>
        )}

        {state.positions.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay cargos. Agrega el primero.</p>}

        {state.positions.map((p) => {
          const races = racesOfPosition(state, p.id);
          const step = positionStep(state, p);
          const winnerName = candidateName(state, p.winner_candidate_id);
          return (
            <div key={p.id} className="space-y-3 rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{p.title}</h3>
                  {p.status === 'FILLED' && <Badge>Elegido: {winnerName}</Badge>}
                  {p.status === 'VOTING' && <Badge variant="outline">En votación</Badge>}
                  {p.status === 'PENDING' && <Badge variant="secondary">Pendiente</Badge>}
                </div>
                <div className="flex gap-2">
                  {step.kind === 'NEW_ROUND' && can('election_race.create') && !closed && (
                    <Button size="sm" onClick={() => setDialog({ position: p, race: null })}>
                      <Plus className="mr-1.5 size-4" /> {step.first ? 'Crear primera ronda' : 'Nueva ronda'}
                    </Button>
                  )}
                  {p.status === 'PENDING' && races.length === 0 && can('election_race.edit') && (
                    <Button size="icon" variant="ghost" title="Eliminar cargo" onClick={() => removePosition.mutate(p.id)}>
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>

              {races.map((r) => (
                <RoundCard
                  key={r.id}
                  state={state}
                  race={r}
                  fetchedAt={fetchedAt}
                  awaitingConfirmation={step.kind === 'CONFIRM_WINNER' && step.race.id === r.id}
                  onEdit={(race) => setDialog({ position: p, race })}
                />
              ))}
            </div>
          );
        })}
      </CardContent>

      {dialog && (
        <RoundDialog
          open
          onOpenChange={(o) => !o && setDialog(null)}
          state={state}
          positionId={dialog.position.id}
          positionTitle={dialog.position.title}
          race={dialog.race}
          busy={saveRound.isPending}
          onSubmit={(ids, settings) =>
            saveRound.mutate(
              { race: dialog.race, positionId: dialog.position.id, ids, settings },
              { onSuccess: () => setDialog(null) },
            )
          }
        />
      )}
    </Card>
  );
}
